import { Box, Button, IconButton, Modal, TextField, Typography } from '@mui/material'
import React from 'react'
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';

// ── Theme tokens (same as Dashboard) ─────────────────────────────────────────
const ACCENT_BLUE = '#22638f';
const ACCENT_BLUE_HOVER = '#1b4f73';
const ACCENT_ORANGE = '#d96c3c';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';
const SOFT_BG = '#f7f9fc';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 'calc(100% - 32px)', sm: 600 },
    maxHeight: 'calc(100vh - 48px)',
    overflowY: 'auto',
    bgcolor: '#fff',
    borderRadius: '16px',
    border: `1px solid ${BORDER}`,
    boxShadow: '0 20px 50px rgba(12, 61, 110, 0.18)',
    outline: 'none',
};

// Text field look shared by all three inputs
const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        '& fieldset': { borderColor: '#dfe5ee' },
        '&:hover fieldset': { borderColor: '#b9c6d8' },
        '&.Mui-focused fieldset': { borderColor: ACCENT_BLUE, borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { color: TEXT_MUTED, fontSize: 14 },
    '& .MuiInputLabel-root.Mui-focused': { color: ACCENT_BLUE },
    '& .MuiInputLabel-asterisk': { color: '#d32f2f' },
    '& .MuiFormHelperText-root': { marginLeft: '2px' },
};

const sliderSx = {
    color: ACCENT_BLUE,
    height: 4,
    '& .MuiSlider-thumb': {
        width: 16,
        height: 16,
        backgroundColor: '#fff',
        border: `2px solid ${ACCENT_BLUE}`,
        '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(34, 99, 143, 0.12)' },
    },
    '& .MuiSlider-rail': { opacity: 1, backgroundColor: '#dde6f0' },
};

// One labelled slider: "Area %  ······  50%"
function ThresholdSlider({ label, value, onChange }) {
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: TEXT_MUTED, fontWeight: 500 }}>{label}</Typography>
                <Box sx={{
                    fontSize: 12.5, fontWeight: 700, color: ACCENT_BLUE,
                    backgroundColor: 'rgba(34, 99, 143, 0.08)', borderRadius: '6px', px: 1, py: 0.25,
                    minWidth: 44, textAlign: 'center',
                }}>
                    {value}%
                </Box>
            </Box>
            <Slider value={value} onChange={onChange} min={0} max={100} aria-label={label} sx={sliderSx} />
        </Box>
    );
}

// A threshold group: title + Area/Quality sliders side by side
function ThresholdGroup({ title, area, quality, onAreaChange, onQualityChange }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: TEXT_BODY, mb: 1 }}>{title}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 1 }}>
                <ThresholdSlider label="Area %" value={area} onChange={onAreaChange} />
                <ThresholdSlider label="Quality %" value={quality} onChange={onQualityChange} />
            </Box>
        </Box>
    );
}

function AddCustomVariety({ open, handleSave, handleClose, heightOfKernel, setHeightOfKernel, PoniyaThreshold, setPoniyaThreshold, brokenThreshold, setBrokenThreshold, handleCheckboxChange, addThreshold,
    chalkyAreaForVariety, chalkyQualityForVariety, discolorAreaForVariety, discolorQualityForVariety, handleSliderforChalkyAreaForVariety, handleSliderForChalkyQualityForVariety, handleSliderforDiscolourAreaForVariety, handleSliderforDiscolourQualityForVariety }) {

    // Allow free typing; show errors inline instead of alert() on every keystroke
    const handleHeightChange = (e) => {
        const v = e.target.value;
        if (/^\d*\.?\d{0,3}$/.test(v)) setHeightOfKernel(v);
    };
    const handlePoniyaThresholdChange = (e) => {
        const v = e.target.value;
        if (/^\d*$/.test(v)) setPoniyaThreshold(v);
    };
    const handleBrokenThresholdChange = (e) => {
        const v = e.target.value;
        if (/^\d*$/.test(v)) setBrokenThreshold(v);
    };

    const heightError = heightOfKernel !== '' && heightOfKernel != null && !(parseFloat(heightOfKernel) > 0 && parseFloat(heightOfKernel) < 15);
    const brokenError = brokenThreshold !== '' && PoniyaThreshold !== '' && parseInt(brokenThreshold, 10) >= parseInt(PoniyaThreshold, 10);
    const canSave = heightOfKernel !== '' && PoniyaThreshold !== '' && brokenThreshold !== '' && !heightError && !brokenError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            disableEnforceFocus
            slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(15, 30, 50, 0.45)', pointerEvents: 'none' } } }}
        >
            <Box sx={modalStyle}>
                {/* Header */}
                <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 2, backgroundColor: ACCENT_ORANGE }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: TEXT_BODY, lineHeight: 1.3 }}>
                            Add custom variety
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: TEXT_MUTED, mt: 0.25 }}>
                            Rice · set kernel size and grading thresholds
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close"
                        onClick={handleClose}
                        size="small"
                        sx={{ color: TEXT_MUTED, borderRadius: '8px', '&:hover': { backgroundColor: SOFT_BG, color: TEXT_BODY } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        label="Perfect height of a kernel (mm)"
                        value={heightOfKernel ?? ''}
                        onChange={handleHeightChange}
                        inputProps={{ inputMode: 'decimal' }}
                        error={heightError}
                        helperText={heightError ? 'Enter a height above 0 and below 15 mm' : ' '}
                        sx={fieldSx}
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: -1 }}>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Poniya kernel threshold (%)"
                            value={PoniyaThreshold ?? ''}
                            onChange={handlePoniyaThresholdChange}
                            inputProps={{ inputMode: 'numeric' }}
                            helperText=" "
                            sx={fieldSx}
                        />
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Broken kernel threshold (%)"
                            value={brokenThreshold ?? ''}
                            onChange={handleBrokenThresholdChange}
                            inputProps={{ inputMode: 'numeric' }}
                            error={brokenError}
                            helperText={brokenError ? `Must be less than ${PoniyaThreshold}%` : ' '}
                            sx={fieldSx}
                        />
                    </Box>

                    <FormControlLabel
                        sx={{ mt: -1, ml: -1, '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 600, color: TEXT_BODY } }}
                        control={
                            <Checkbox
                                checked={addThreshold}
                                onChange={handleCheckboxChange}
                                size="small"
                                sx={{ color: '#b9c6d8', '&.Mui-checked': { color: ACCENT_BLUE } }}
                            />
                        }
                        label="Add chalky & discolour thresholds"
                    />

                    {addThreshold && (
                        <Box sx={{
                            backgroundColor: SOFT_BG, border: `1px solid ${BORDER}`, borderRadius: '12px',
                            p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5,
                        }}>
                            <ThresholdGroup
                                title="Chalky threshold"
                                area={chalkyAreaForVariety}
                                quality={chalkyQualityForVariety}
                                onAreaChange={handleSliderforChalkyAreaForVariety}
                                onQualityChange={handleSliderForChalkyQualityForVariety}
                            />
                            <Box sx={{ height: '1px', backgroundColor: '#e6ebf2' }} />
                            <ThresholdGroup
                                title="Discolour threshold"
                                area={discolorAreaForVariety}
                                quality={discolorQualityForVariety}
                                onAreaChange={handleSliderforDiscolourAreaForVariety}
                                onQualityChange={handleSliderforDiscolourQualityForVariety}
                            />
                        </Box>
                    )}
                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 2.5, height: 40,
                            color: TEXT_BODY, borderColor: '#dfe5ee',
                            '&:hover': { borderColor: '#b9c6d8', backgroundColor: SOFT_BG },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!canSave}
                        sx={{
                            borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3, height: 40,
                            backgroundColor: ACCENT_BLUE, boxShadow: 'none',
                            '&:hover': { backgroundColor: ACCENT_BLUE_HOVER, boxShadow: 'none' },
                        }}
                    >
                        Save variety
                    </Button>
                </Box>
            </Box>
        </Modal>
    )
}

export default AddCustomVariety